import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  NgZone,
  signal,
  untracked,
} from '@angular/core';
import { CartService } from '../../services/cart.service';

const INTERVALO_MS = 1000;
// Últimos 60s: el pulso se acelera y cambia de color para avisar que está por vencer.
const UMBRAL_URGENTE_MS = 60_000;

// Cuenta regresiva de la ventana de 5 min del carrito (ver CartService.createdAt). Solo se
// muestra mientras hay productos en el carrito (expiresAt no nulo); vacío, no ocupa lugar ni
// corre ningún timer.
@Component({
  selector: 'app-cart-countdown',
  standalone: true,
  templateUrl: './cart-countdown.html',
  styleUrl: './cart-countdown.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartCountdown {
  private readonly cart = inject(CartService);
  private readonly ngZone = inject(NgZone);
  // expiresAt cambia pocas veces, pero el "mm:ss" que se muestra cambia cada segundo, y eso no es algo
  // derivable de otros signals: necesita el paso del reloj real, de ahí este tick propio.
  private readonly ahora = signal(Date.now());
  private intervaloId: ReturnType<typeof setInterval> | null = null;

  readonly restanteMs = computed(() => {
    const expira = this.cart.expiresAt();
    return expira === null ? null : Math.max(0, expira - this.ahora());
  });

  readonly visible = computed(() => this.restanteMs() !== null);
  readonly urgente = computed(() => (this.restanteMs() ?? Infinity) <= UMBRAL_URGENTE_MS);

  readonly texto = computed(() => {
    const restante = this.restanteMs();
    if (restante === null) {
      return '';
    }
    const totalSeg = Math.ceil(restante / 1000);
    const min = Math.floor(totalSeg / 60);
    const seg = totalSeg % 60;
    return `${min}:${seg.toString().padStart(2, '0')}`;
  });

  readonly mensaje = computed(() => `Tu carrito se vaciará en ${this.texto()} si no completás la compra.`);

  constructor() {
    // El tick de 1s solo corre mientras hay una ventana activa (expiresAt no nulo) — nada de
    // dejar un setInterval corriendo siempre, esto se renderiza en el header de toda la app.
    // Fuera de la zona de Angular por la misma razón que el timer de expiración del cart: no
    // debe mantener la app "inestable" (whenStable(), etc.) solo por estar tickeando.
    effect(() => {
      const expira = this.cart.expiresAt();
      untracked(() => this.reprogramarTick(expira));
    });
    inject(DestroyRef).onDestroy(() => this.detenerTick());
  }

  private reprogramarTick(expira: number | null): void {
    this.detenerTick();
    if (expira === null) {
      return;
    }
    this.ahora.set(Date.now());
    // Fuera de la zona para no mantenerla "ocupada" (whenStable(), etc.) solo por tickear cada
    // segundo. El signal write alcanza igual para refrescar la vista: Angular programa el
    // render por su cuenta cuando cambia un signal que el template lee, sin depender de NgZone.
    this.ngZone.runOutsideAngular(() => {
      this.intervaloId = setInterval(() => this.ahora.set(Date.now()), INTERVALO_MS);
    });
  }

  private detenerTick(): void {
    if (this.intervaloId !== null) {
      clearInterval(this.intervaloId);
      this.intervaloId = null;
    }
  }
}
